import { FormattedMessage } from 'react-intl-next';
import React from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import { expValEquals } from '@confluence/feature-experiments';

import { CREATE_WHITEBOARD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getTraceIdFromApolloError } from '@confluence/template-utils/entry-points/errorUtils.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import {
	CREATED_FROM_SLUG,
	INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM,
} from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import { EDIT_PAGE_V2 } from '@confluence/named-routes/entry-points/routes.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import { messages } from './messages.ts';
import { getExpectedError } from './error-handling.ts';
import { createWhiteboardOverGraphQL } from './create-whiteboard.ts';

export const createWhiteboardQuickInsertModule = (
	pageId: string,
	spaceId: string,
	spaceKey: string,
	experienceTracker?: ExperienceTrackerAPI,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	flags?: FlagsStateContainer,
	templateKey?: string,
	pageTreeStateUpdater?: PageTreeStateUpdater,
) => {
	return async () => {
		try {
			const instrumentedTemplateKey = templateKey ?? 'blank';

			experienceTracker?.start({
				name: CREATE_WHITEBOARD_EXPERIENCE,
				timeout: ExperienceTimeout.CREATE_CONTENT,
				attributes: { flow: 'inlinePageCreation', template: instrumentedTemplateKey },
			});

			// call API to create whiteboard
			const { fullUrl, contentId } = await createWhiteboardOverGraphQL(
				pageId,
				spaceId,
				spaceKey,
				instrumentedTemplateKey,
				createAnalyticsEvent,
			);
			// allow user to navigate directly to whiteboard
			await flags?.showFlag({
				type: 'success-circle',
				title: <FormattedMessage {...messages.successFlagTitle} />,
				actions: [
					{
						content: <FormattedMessage {...messages.successFlagAction} />,
						href: fullUrl,
					},
				],
				description: <FormattedMessage {...messages.successFlagDescription} />,
			});

			if (pageTreeStateUpdater && contentId) {
				pageTreeStateUpdater.setForceFetchPageId(pageId);
			}

			experienceTracker?.succeed({ name: CREATE_WHITEBOARD_EXPERIENCE });

			const url = new URL(fullUrl);
			const originatingUrl = EDIT_PAGE_V2.toUrl({
				contentType: 'pages',
				spaceKey,
				contentId: pageId,
			});

			if (templateKey) {
				// for applying a specific template once opened
				url.searchParams.set(INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM, templateKey);
			}

			// for reconstructing the URL on the other side
			url.searchParams.set(CREATED_FROM_SLUG, originatingUrl);

			window.open(url, '_blank', 'noreferrer, noopener');

			if (expValEquals('cc-maui-experiment', 'isEnabled', true)) {
				return {
					type: 'extension',
					attrs: {
						extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:whiteboard`,
						extensionType: NATIVE_EMBED_EXTENSION_TYPE,
						parameters: {
							macroParams: {
								url: { value: fullUrl },
							},
						},
					},
				};
			}

			return { type: 'embedCard', attrs: { url: fullUrl } };
		} catch (error) {
			const expectedErrorMessage = getExpectedError(error);
			if (expectedErrorMessage) {
				await flags?.showErrorFlag({
					title: <FormattedMessage {...expectedErrorMessage.title} />,
					description: <FormattedMessage {...expectedErrorMessage.description} />,
				});

				experienceTracker?.succeed({
					name: CREATE_WHITEBOARD_EXPERIENCE,
					attributes: { abortReason: 'expectedErrorMessage' },
				});
			} else {
				const monitoringClient = getMonitoringClient();
				monitoringClient.submitError(error, {
					attribution: Attribution.WHITEBOARDS,
				});

				await flags?.showErrorFlag({
					title: <FormattedMessage {...messages.whiteboardDefaultErrorFlagTitle} />,
					description: <FormattedMessage {...messages.whiteboardDefaultErrorFlagDescription} />,
				});

				experienceTracker?.stopOnError({
					name: CREATE_WHITEBOARD_EXPERIENCE,
					error,
					attributes: { cteTraceId: getTraceIdFromApolloError(error) },
				});
			}
			markErrorAsHandled(error);
		}
	};
};
